// DeepgramService.ts
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk'
import Logger from '@/services/Logger.js'
import AxiosInstance from '@/services/AxiosInstance.js'
import FirebaseInstance from '@/services/FirebaseInstance.js'
import type { TranscriptionResult } from '@/customTypes'
import { useDataStore } from '@/stores/DataStore'

const LIVE_TRANSCRIPTION_KEY_URL = `${_API_BASE_URL_}/screening/pupil-session/live-transcription-key`
const DEEPGRAM_REACHABILITY_URL = 'https://api.deepgram.com/v1/listen'
// Fetch a new key while the current one still has time to open a connection.
const KEY_REFRESH_MARGIN_MS = 2 * 60 * 1000
const CONNECTION_CHECK_TIMEOUT_MS = 5000

type LiveTranscriptionKey = {
  key: string
  expiresAt: number
  sessionUid: string
}

// Shared by every DeepgramService instance, and dropped when a different pupil signs in.
let cachedKey: LiveTranscriptionKey | null = null
let pendingKeyRequest: Promise<string> | null = null

const currentSessionUid = (): string | null => FirebaseInstance.auth().currentUser?.uid || null

const isCachedKeyUsable = (sessionUid: string): boolean =>
  cachedKey !== null &&
  cachedKey.sessionUid === sessionUid &&
  cachedKey.expiresAt - Date.now() > KEY_REFRESH_MARGIN_MS

/**
 * Returns a short-lived Deepgram key for the signed-in pupil session, fetching a new one from
 * the API when the cached key is missing, near expiry or belongs to another session.
 */
const getLiveTranscriptionKey = async (): Promise<string> => {
  const sessionUid = currentSessionUid()
  if (!sessionUid) {
    throw new Error('No pupil session to create a live transcription key for')
  }

  if (isCachedKeyUsable(sessionUid)) {
    return cachedKey!.key
  }

  if (!pendingKeyRequest) {
    pendingKeyRequest = AxiosInstance.post(LIVE_TRANSCRIPTION_KEY_URL)
      .then((response: { key: string; expiresAt: string }) => {
        cachedKey = {
          key: response.key,
          expiresAt: new Date(response.expiresAt).getTime(),
          sessionUid
        }
        return response.key
      })
      .finally(() => {
        pendingKeyRequest = null
      })
  }

  return pendingKeyRequest as Promise<string>
}

export type DeepgramConnectionHandle = {
  connection: any
  questionId: number
  disposed: boolean
}

export class DeepgramService {
  private logger: any
  private lastProcessedIndex: number = 0

  constructor() {
    this.logger = new Logger('DeepgramService.ts', true)
  }

  /**
   * Creates a connection to the Deepgram service.
   * @param {number} sampleRate - The sample rate of the audio.
   * @returns {Promise<any>} A promise that resolves to the Deepgram connection.
   */
  async createConnection(sampleRate: number = 44100, keywords: string[]) {
    const deepgram = createClient(await getLiveTranscriptionKey())
    return await deepgram.listen.live({
      model: 'nova-2',
      punctuate: false,
      language: 'en-GB',
      encoding: 'linear16',
      sample_rate: sampleRate,
      interim_results: true,
      keywords: keywords
    })
  }

  // questionId is closure-captured so late transcripts route after the active question moves on.
  setupConnectionListeners(
    connection: any,
    questionId: number,
    routeTranscript: (questionId: number, result: TranscriptionResult) => void,
    findMatches: (text: string) => TranscriptionResult,
    onError?: (error: unknown) => void
  ): DeepgramConnectionHandle {
    const handle: DeepgramConnectionHandle = { connection, questionId, disposed: false }

    connection.on(LiveTranscriptionEvents.Transcript, (data: any) => {
      if (handle.disposed) return
      const text = data.channel.alternatives[0].transcript
      if (text) {
        const result = findMatches(text)
        routeTranscript(questionId, result)
      }
    })

    connection.on(LiveTranscriptionEvents.Error, (error: any) => {
      if (handle.disposed) return
      this.logger.error('Deepgram error:', error)
      if (onError) {
        onError(error)
      }
    })

    return handle
  }

  closeHandle(handle: DeepgramConnectionHandle | null) {
    if (!handle) return
    handle.disposed = true
    if (handle.connection) {
      handle.connection.finish()
    }
  }

  /**
   * Sends audio data to the Deepgram service.
   * @param {any} connection - The Deepgram connection.
   * @param {Float32Array} audioData - The audio data to send.
   */
  sendAudioData(connection: any, audioData: Float32Array) {
    const int16Array = new Int16Array(audioData.map((x) => x * 32767))
    const uint8Array = new Uint8Array(int16Array.buffer)
    if (connection) {
      connection.send(uint8Array)
    }
  }

  /**
   * Checks the connection to the Deepgram service. Before a pupil has entered a code there is no
   * key to stream with, so only Deepgram's reachability is checked.
   * @returns {Promise<boolean>} A promise that resolves to true if the connection is successful, false otherwise.
   */
  async checkConnection(): Promise<boolean> {
    if (!currentSessionUid()) {
      return this.checkReachability()
    }

    let key: string
    try {
      key = await getLiveTranscriptionKey()
    } catch (error) {
      this.logger.error('Deepgram connection check could not get a key:', error)
      return false
    }

    return this.checkLiveConnection(key)
  }

  /**
   * Checks Deepgram can be reached over HTTPS. Any response counts: only a blocked or failed
   * request means the network cannot reach it.
   */
  async checkReachability(): Promise<boolean> {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CONNECTION_CHECK_TIMEOUT_MS)

    try {
      await fetch(DEEPGRAM_REACHABILITY_URL, {
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      })
      return true
    } catch (error) {
      this.logger.error('Deepgram reachability check failed:', error)
      return false
    } finally {
      clearTimeout(timeout)
    }
  }

  checkLiveConnection(key: string): Promise<boolean> {
    return new Promise((resolve) => {
      let connection: any = null

      const timeout = setTimeout(() => {
        this.logger.error('Deepgram connection check timed out.')
        if (connection) {
          this.stopConnection(connection)
        }
        resolve(false)
      }, CONNECTION_CHECK_TIMEOUT_MS)

      try {
        const deepgram = createClient(key)
        connection = deepgram.listen.live({
          model: 'nova-2',
          punctuate: false,
          language: 'en-GB',
          encoding: 'linear16',
          sample_rate: 44100,
          interim_results: true,
          keywords: []
        })

        connection.on(LiveTranscriptionEvents.Open, () => {
          clearTimeout(timeout)
          this.stopConnection(connection)
          resolve(true)
        })

        connection.on(LiveTranscriptionEvents.Error, (error: any) => {
          clearTimeout(timeout)
          this.logger.error('Deepgram connection check failed with error:', error)
          this.stopConnection(connection)
          resolve(false)
        })

        connection.on(LiveTranscriptionEvents.Close, () => {
          clearTimeout(timeout)
          const dataStore = useDataStore()
          if (dataStore.screenerInProgress) {
            this.logger.error('Deepgram connection check failed, connection closed.')
          }
          resolve(false)
        })
      } catch (error) {
        clearTimeout(timeout)
        this.logger.error('Deepgram connection check failed:', error)
        resolve(false)
      }
    })
  }

  /**
   * Stops the Deepgram connection.
   * @param {any} connection - The Deepgram connection to stop.
   */
  stopConnection(connection: any) {
    if (connection) {
      connection.finish()
    }
  }
}
